import { motion } from 'motion/react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowsUpDownIcon,
  TrashIcon,
  StarIcon,
  ArrowUturnLeftIcon,
  FolderArrowDownIcon,
  CloudArrowUpIcon,
  FolderPlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useFileStore } from '../../stores/fileStore';
import { useUIStore } from '../../stores/uiStore';
import { filesApi } from '../../api/files';
import { useToast } from '../ui/Toaster';
import { Button } from '../ui/Button';

export function FileToolbar() {
  const { t } = useTranslation('files');
  const {
    sortBy,
    setSortBy,
    selectedIds,
    clearSelection,
    removeFile,
  } = useFileStore();
  const openDeleteConfirm = useUIStore((s) => s.openDeleteConfirm);
  const openMoveModal = useUIStore((s) => s.openMoveModal);
  const openUploadModal = useUIStore((s) => s.openUploadModal);
  const openCreateFolderModal = useUIStore((s) => s.openCreateFolderModal);
  const openCreateDocumentModal = useUIStore((s) => s.openCreateDocumentModal);
  const toast = useToast();
  const location = useLocation();

  const isTrashPage = location.pathname === '/trash';
  const isStarredPage = location.pathname === '/starred';
  const hasSelection = selectedIds.length > 0;

  const handleDelete = async () => {
    if (isTrashPage) {
      // Open confirmation dialog for permanent delete
      openDeleteConfirm();
    } else {
      const ids = [...selectedIds]; // Snapshot before async
      try {
        await filesApi.bulkAction({ fileIds: ids, action: 'delete' });
        ids.forEach((id) => removeFile(id));
        clearSelection();
        toast.success(t('toast.movedToTrash'));
      } catch {
        toast.error(t('toast.deleteFailed'));
      }
    }
  };

  const handleRestore = async () => {
    const ids = [...selectedIds]; // Snapshot before async
    try {
      await filesApi.bulkAction({ fileIds: ids, action: 'restore' });
      ids.forEach((id) => removeFile(id));
      clearSelection();
      toast.success(t('toast.filesRestored'));
    } catch {
      toast.error(t('toast.restoreFailed'));
    }
  };

  const handleStar = async () => {
    const ids = [...selectedIds]; // Snapshot before async
    try {
      await filesApi.bulkAction({ fileIds: ids, action: 'star' });
      toast.success(t('toast.addedToStarred'));
      clearSelection();
    } catch {
      toast.error(t('toast.starFailed'));
    }
  };

  const toggleSort = () => {
    if (sortBy === 'updatedAt') {
      setSortBy('name');
    } else if (sortBy === 'name') {
      setSortBy('size');
    } else {
      setSortBy('updatedAt');
    }
  };

  return (
    <div className="flex items-center justify-between">
      {/* Left side - selection actions */}
      <div className="flex items-center gap-2">
        {hasSelection ? (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
          >
            <span className="text-sm text-gray-600 dark:text-slate-300">
              {t('toolbar.selected', { count: selectedIds.length })}
            </span>
            {isTrashPage ? (
              <>
                <Button variant="ghost" size="sm" onClick={handleRestore}>
                  <ArrowUturnLeftIcon className="w-4 h-4" />
                  {t('common:buttons.restore')}
                </Button>
                <Button variant="danger" size="sm" onClick={handleDelete}>
                  <TrashIcon className="w-4 h-4" />
                  {t('toolbar.deleteForever')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={handleStar}>
                  <StarIcon className="w-4 h-4" />
                  {t('contextMenu.star')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => openMoveModal(selectedIds)}>
                  <FolderArrowDownIcon className="w-4 h-4" />
                  {t('common:buttons.move')}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDelete}>
                  <TrashIcon className="w-4 h-4" />
                  {t('common:buttons.delete')}
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {t('common:buttons.cancel')}
            </Button>
          </motion.div>
        ) : (
          <button
            onClick={toggleSort}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <ArrowsUpDownIcon className="w-4 h-4" />
            {t('toolbar.sortBy')}{' '}
            {sortBy === 'updatedAt'
              ? t('toolbar.sortDate')
              : sortBy === 'name'
              ? t('toolbar.sortName')
              : t('toolbar.sortSize')}
          </button>
        )}
      </div>

      {/* Right side - Upload, New Folder, and Create Document buttons */}
      {!isTrashPage && !isStarredPage && !hasSelection && (
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={openCreateDocumentModal}>
            <SparklesIcon className="w-4 h-4" />
            {t('toolbar.createDocument')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => openUploadModal()}>
            <CloudArrowUpIcon className="w-4 h-4" />
            {t('common:buttons.upload')}
          </Button>
          <Button variant="secondary" size="sm" onClick={openCreateFolderModal}>
            <FolderPlusIcon className="w-4 h-4" />
            {t('toolbar.newFolder')}
          </Button>
        </div>
      )}
    </div>
  );
}
